<template>
    <div
        ref="rootEl"
        class="mp-dock-root"
        :class="{ 'mp-dock-dragging': dockDrag.active }"
    >
        <div class="mp-dock-toolbar row items-center">
            <slot name="toolbar" />
            <DockHiddenPanelsMenu
                :hidden-panel-ids="hiddenPanelIdsForMenu"
                :get-panel-meta="getPanelMeta"
                @show="showPanel"
            />
        </div>
        <div class="mp-dock-body">
            <DockLayoutRenderer
                v-if="layoutState.root"
                :key="layoutRenderKey"
                :node="layoutState.root"
            />
            <div v-else class="text-grey q-pa-md">No panels visible</div>
        </div>
        <div class="mp-dock-slot-registry" aria-hidden="true">
            <slot />
        </div>
        <DockDropOverlay
            :active="dockDrag.active"
            :drop-zones="dockDrag.dropZones"
            :ghost="dockDrag.ghost"
            :hover-zone-key="dockDrag.hoverZoneKey"
            @drop="onDropZoneDrop"
            @hover="onDropZoneHover"
        />
    </div>
</template>

<script>
import { createVNode, render as vueRender } from 'vue';
import { debounce } from 'lodash';
import DockLayoutRenderer from './MultipanelDockLayout/DockLayoutRenderer.vue';
import DockDropOverlay from './MultipanelDockLayout/DockDropOverlay.vue';
import DockHiddenPanelsMenu from './MultipanelDockLayout/DockHiddenPanelsMenu.vue';
import MultipanelDockPanel from './MultipanelDockLayout/MultipanelDockPanel.vue';
import { exportLayout, importLayout } from './MultipanelDockLayout/layoutSerialize.js';
import {
    applyPanelKindsToTabs,
    ensureLayoutStateRegions,
    findRegionForNodeId,
} from './MultipanelDockLayout/layoutRegions.js';
import { buildZoneBlueprintFromBlock } from './MultipanelDockLayout/blueprintCollect.js';
import {
    DOCK_DRAG_THRESHOLD_PX,
    DEFAULT_EDGE_WRAP_ORDER,
    createEmptyRegions,
    normalizePanelKind,
    PANEL_KIND_CENTER,
    CENTER_REGION_ID,
    TAB_PLACEMENT_TOP,
} from './MultipanelDockLayout/constants.js';
import { buildDropZoneList, hitTestDropZone } from './MultipanelDockLayout/dockDropTargets.js';
import {
    createDockConstraintContext,
    getPanelZoneMap,
    resolveRootRegionFromHit,
} from './MultipanelDockLayout/dockModel.js';
import {
    canDockPanelAtRootAwareHit,
    canDockPanelsInRootRegion,
    canDockPanelInRootRegion,
    resolvePanelPolicy,
} from './MultipanelDockLayout/dockPolicyEngine.js';
import { buildDropIntent } from './MultipanelDockLayout/dropIntent.js';
import { reduceDockDrop } from './MultipanelDockLayout/dockReducer.js';
import {
    cloneTree,
    collectPanelIds,
    setSplitSizes,
    setActiveTab,
    reorderTabPanels,
    moveTabBetweenGroups,
    hidePanelInState,
    showPanelInState,
    closePanelInState,
    appendPanelToTabsGroup,
    findCenterDynamicTabsNode,
    createTabsNode,
    setCenterSlotChild,
    findNode,
    syncRegionsAfterChange,
} from './MultipanelDockLayout/layoutTree.js';
import './MultipanelDockLayout/multipanelDockLayout.scss';

const PERSIST_PREFIX = 'multipanel-dock-layout:';

export default {
    name: 'MultipanelDockLayout',

    components: {
        DockLayoutRenderer,
        DockDropOverlay,
        DockHiddenPanelsMenu,
        MultipanelDockPanel,
    },

    props: {
        layout: { type: Object, default: null },
        defaultOrientation: {
            type: String,
            default: 'horizontal',
            validator: (v) => ['horizontal', 'vertical'].includes(v),
        },
        debounceMs: { type: Number, default: 450 },
        persistKey: { type: String, default: null },
    },

    emits: [
        'update:layout',
        'layout-change',
        'panel-activate',
        'panel-hide',
        'panel-show',
        'tab-change',
        'dynamic-tab-add',
        'dynamic-tab-close',
    ],

    expose: [
        'serializeLayout',
        'deserializeLayout',
        'resetLayout',
        'hidePanel',
        'showPanel',
        'closeDynamicTab',
        'addCenterDynamicTab',
        'setPanelPinned',
        'getPanelState',
    ],

    provide() {
        return {
            multipanelDockInheritedZone: null,
            multipanelDockInheritedRestrictions: null,
            multipanelDockLayout: {
                registerPanel: (def) => this.registerPanel(def),
                unregisterPanel: (id) => this.unregisterPanel(id),
                registerZoneBlock: (key, block) =>
                    this.registerZoneBlock(key, block),
                unregisterZoneBlock: (key) => this.unregisterZoneBlock(key),
                registerTabGroup: (key, def) => this.registerTabGroup(key, def),
                unregisterTabGroup: (key) => this.unregisterTabGroup(key),
                getTabGroupPanelIds: (key) => this.getTabGroupPanelIds(key),
                getPanelMeta: (id) => this.getPanelMeta(id),
                getPanelKind: (id) => this.getPanelKind(id),
                isPanelPinned: (id) => this.isPanelPinned(id),
                isPanelCollapsed: (id) => this.isPanelCollapsed(id),
                mountPanelToTarget: (id, el) => this.mountPanelToTarget(id, el),
                unmountPanelFromTarget: (id) => this.unmountPanelFromTarget(id),
                setPanelHostEl: (id, el) => this.setPanelHostEl(id, el),
                hidePanel: (id) => this.hidePanel(id),
                closePanel: (id) => this.closePanel(id),
                showPanel: (id) => this.showPanel(id),
                expandPanel: (id) => this.expandPanel(id),
                togglePanelPinned: (id) => this.togglePanelPinned(id),
                updateSplitSizes: (splitId, sizes) =>
                    this.updateSplitSizes(splitId, sizes),
                setActiveTab: (tabsId, panelId) =>
                    this.setActiveTab(tabsId, panelId),
                onTabDrop: (result) => this.onTabDrop(result),
                startDockDrag: (payload) => this.startDockDrag(payload),
                onPanelFocusChange: (p) => this.onPanelFocusChange(p),
            },
        };
    },

    data() {
        return {
            layoutState: {
                version: 2,
                root: null,
                regions: createEmptyRegions(),
                panels: {},
                hiddenPanelIds: [],
                edgeWrapOrder: [...DEFAULT_EDGE_WRAP_ORDER],
                rootEdgeOrder: [...DEFAULT_EDGE_WRAP_ORDER],
            },
            panelRegistry: {},
            zoneBlocks: {},
            zoneBlockChildren: {},
            tabGroups: {},
            tabGroupPanelIds: {},
            tabGroupContentSlots: {},
            panelHostEls: {},
            panelMounts: {},
            collapsedPanels: {},
            dockDrag: {
                active: false,
                panelId: null,
                dragMode: 'panel',
                groupNodeId: null,
                panelIds: [],
                dropZones: [],
                hoverZoneKey: null,
                commitHit: null,
                forbiddenHover: false,
                ghost: null,
            },
            layoutRenderKey: 0,
            _initialized: false,
            _initScheduled: false,
            _debouncedEmit: null,
            _pendingDrag: null,
            _dragPanelId: null,
            _lastPointerX: 0,
            _lastPointerY: 0,
        };
    },

    computed: {
        hiddenPanelIdsForMenu() {
            return (this.layoutState.hiddenPanelIds || []).filter((id) => {
                const reg = this.panelRegistry[id];
                return reg?.listInPanelsMenu !== false;
            });
        },
        ephemeralPanelIds() {
            const ids = new Set();
            for (const id of Object.keys(this.panelRegistry)) {
                if (this.panelRegistry[id]?.ephemeral) ids.add(id);
            }
            return ids;
        },
    },

    watch: {
        layout: {
            deep: true,
            handler(val) {
                if (!this._initialized || !val) return;
                const ids = Object.keys(this.panelRegistry).filter(
                    (id) => !this.panelRegistry[id]?.isContentHost,
                );
                const imported = importLayout(val, {
                    registeredPanelIds: ids,
                    defaultOrientation: this.defaultOrientation,
                    panelZones: this.getPanelZonesMap(),
                    panelKinds: this.getPanelKindsMap(),
                    regionBlueprints: this.assembleRegionBlueprints(),
                });
                this.registerTabGroupContentSlots(imported.root);
                this.applyLayoutState(imported, { skipEmit: true });
            },
        },
    },

    created() {
        this._debouncedEmit = debounce(() => {
            const payload = exportLayout(this.layoutState, {
                ephemeralPanelIds: this.ephemeralPanelIds,
            });
            this.$emit('update:layout', payload);
            this.$emit('layout-change', payload);
            if (this.persistKey) {
                try {
                    localStorage.setItem(
                        PERSIST_PREFIX + this.persistKey,
                        JSON.stringify(payload),
                    );
                } catch (_) {
                    /* ignore */
                }
            }
        }, this.debounceMs);
    },

    mounted() {
        this.scheduleInitializeLayout();
    },

    beforeUnmount() {
        this.teardownDockDrag();
        this._debouncedEmit?.cancel?.();
        for (const id of Object.keys(this.panelMounts)) {
            this.unmountPanelFromTarget(id);
        }
    },

    methods: {
        getPanelZonesMap() {
            return getPanelZoneMap(this.layoutState, this.panelRegistry);
        },

        getPanelKindsMap() {
            const kinds = {};
            for (const id of Object.keys(this.panelRegistry)) {
                const reg = this.panelRegistry[id];
                if (reg?.isContentHost) continue;
                kinds[id] =
                    reg?.panelKind ?? this.layoutState.panels[id]?.panelKind;
            }
            return kinds;
        },

        scheduleInitializeLayout() {
            if (this._initialized || this._initScheduled) return;
            this._initScheduled = true;
            this.$nextTick(() => {
                this.$nextTick(() => {
                    this._initScheduled = false;
                    if (!this._initialized) this.initializeLayout();
                });
            });
        },

        getTabGroupPanelIds(key) {
            return this.tabGroupPanelIds[key] ? [...this.tabGroupPanelIds[key]] : [];
        },

        initializeLayout() {
            const ids = Object.keys(this.panelRegistry).filter(
                (id) => !this.panelRegistry[id]?.isContentHost,
            );
            if (!ids.length) {
                this._initialized = true;
                return;
            }

            let json = this.layout;
            if (!json && this.persistKey) {
                try {
                    const raw = localStorage.getItem(
                        PERSIST_PREFIX + this.persistKey,
                    );
                    if (raw) json = JSON.parse(raw);
                } catch (_) {
                    /* ignore */
                }
            }

            const imported = importLayout(json, {
                registeredPanelIds: ids,
                defaultOrientation: this.defaultOrientation,
                panelZones: this.getPanelZonesMap(),
                panelKinds: this.getPanelKindsMap(),
                regionBlueprints: this.assembleRegionBlueprints(),
            });
            this.registerTabGroupContentSlots(imported.root);

            for (const id of ids) {
                const reg = this.panelRegistry[id];
                if (reg) {
                    imported.panels[id] = {
                        ...(imported.panels[id] || {}),
                        pinned: reg.pinned !== false,
                        panelKind: normalizePanelKind(
                            reg.panelKind,
                            reg.defaultZone,
                        ),
                    };
                }
            }

            this.applyLayoutState(imported, { skipEmit: true });
            this._initialized = true;
            this.emitLayoutChange();
        },

        appendZoneBlockChild(zoneBlockKey, entry) {
            if (!zoneBlockKey || !entry) return;
            if (!this.zoneBlockChildren[zoneBlockKey]) {
                this.zoneBlockChildren[zoneBlockKey] = [];
            }
            const list = this.zoneBlockChildren[zoneBlockKey];
            const exists = list.some(
                (e) =>
                    e.kind === entry.kind &&
                    (entry.kind === 'panel'
                        ? e.id === entry.id
                        : e.key === entry.key),
            );
            if (!exists) list.push(entry);
            const block = this.zoneBlocks[zoneBlockKey];
            if (block) block.children = [...list];
        },

        assembleRegionBlueprints() {
            const regions = {};
            for (const [key, block] of Object.entries(this.zoneBlocks)) {
                const children =
                    this.zoneBlockChildren[key] || block.children || [];
                const node = buildZoneBlueprintFromBlock(
                    { ...block, children },
                    this.tabGroups,
                );
                if (node) regions[block.zone] = node;
            }
            for (const tg of Object.values(this.tabGroups)) {
                if (tg.zoneBlockKey) continue;
                if (regions[tg.zone]) continue;
                regions[tg.zone] = {
                    type: 'tabGroup',
                    groupId: tg.groupId,
                    tabPlacement: tg.tabPlacement,
                    dynamic: tg.dynamic,
                    contentPanelId: tg.contentPanelId,
                    panelIds: tg.panelIds,
                    activePanelId: tg.activePanelId,
                    title: tg.title,
                };
            }
            return regions;
        },

        registerZoneBlock(key, block) {
            if (!key || !block) return;
            const children = this.zoneBlockChildren[key] || block.children || [];
            this.zoneBlocks[key] = { ...block, children: [...children] };
            if (!this._initialized) this.scheduleInitializeLayout();
        },

        unregisterZoneBlock(key) {
            delete this.zoneBlocks[key];
            delete this.zoneBlockChildren[key];
        },

        registerTabGroup(key, def) {
            if (!key || !def) return;
            const panelIds =
                this.tabGroupPanelIds[key]?.length > 0
                    ? this.getTabGroupPanelIds(key)
                    : def.panelIds || [];
            this.tabGroups[key] = { ...def, panelIds };
            if (def.zoneBlockKey) {
                this.appendZoneBlockChild(def.zoneBlockKey, {
                    kind: 'tabGroup',
                    key,
                });
            }
            if (def.contentPanelId && def.contentSlot) {
                this.tabGroupContentSlots[def.contentPanelId] = def.contentSlot;
                const contentPanelId = def.contentPanelId;
                const slotFn = def.contentSlot;
                this.panelRegistry[contentPanelId] = {
                    id: contentPanelId,
                    title: def.title || 'Content',
                    default: () => {
                        const activeTab = this.getDynamicContentActiveTab(
                            contentPanelId,
                        );
                        return slotFn({ activeTab });
                    },
                    isContentHost: true,
                };
            }
            if (!this._initialized && !def.zoneBlockKey) {
                this.scheduleInitializeLayout();
            }
        },

        unregisterTabGroup(key) {
            const tg = this.tabGroups[key];
            if (tg?.contentPanelId) {
                delete this.tabGroupContentSlots[tg.contentPanelId];
                delete this.panelRegistry[tg.contentPanelId];
                this.unmountPanelFromTarget(tg.contentPanelId);
            }
            delete this.tabGroups[key];
            delete this.tabGroupPanelIds[key];
        },

        getDynamicContentActiveTab(contentPanelId) {
            const walk = (node) => {
                if (!node) return null;
                if (
                    node.type === 'tabs' &&
                    node.contentMode === 'dynamic' &&
                    node.contentPanelId === contentPanelId
                ) {
                    const ids = node.panelIds || [];
                    if (ids.includes(node.activePanelId)) {
                        return node.activePanelId;
                    }
                    return ids[0] || null;
                }
                if (node.type === 'split') {
                    for (const ch of node.children || []) {
                        const r = walk(ch);
                        if (r) return r;
                    }
                }
                if (node.type === 'centerSlot' && node.child) {
                    return walk(node.child);
                }
                return null;
            };
            return walk(this.layoutState.root);
        },

        registerTabGroupContentSlots(root) {
            if (!root) return;
            for (const tg of Object.values(this.tabGroups)) {
                if (!tg.dynamic || !tg.contentPanelId || !tg.contentSlot) {
                    continue;
                }
                const contentPanelId = tg.contentPanelId;
                const slotFn = tg.contentSlot;
                this.panelRegistry[contentPanelId] = {
                    id: contentPanelId,
                    title: tg.title || 'Content',
                    default: () => {
                        const activeTab = this.getDynamicContentActiveTab(
                            contentPanelId,
                        );
                        return slotFn({ activeTab });
                    },
                    isContentHost: true,
                };
            }
        },

        registerPanel(def) {
            if (!def?.id) return;
            const policy = resolvePanelPolicy(def);
            this.panelRegistry[def.id] = {
                id: def.id,
                title: def.title,
                icon: def.icon,
                closable: def.closable,
                tabOnly: def.tabOnly,
                ephemeral: def.ephemeral,
                listInPanelsMenu: def.listInPanelsMenu,
                allowedZones: policy.allowed,
                disallowedZones: policy.disallowed,
                allowedRootZones: policy.allowedRootZones,
                defaultZone: def.defaultZone,
                panelKind: def.panelKind,
                pinned: def.pinned,
                default: def.default,
            };
            if (!this.layoutState.panels[def.id]) {
                this.layoutState.panels[def.id] = {
                    pinned: def.pinned !== false,
                    panelKind: normalizePanelKind(
                        def.panelKind,
                        def.defaultZone,
                    ),
                };
            }
            if (def.tabGroupId) {
                if (!this.tabGroupPanelIds[def.tabGroupId]) {
                    this.tabGroupPanelIds[def.tabGroupId] = [];
                }
                const list = this.tabGroupPanelIds[def.tabGroupId];
                if (!list.includes(def.id)) list.push(def.id);
            }
            if (def.zoneBlockKey && !def.tabGroupId) {
                this.appendZoneBlockChild(def.zoneBlockKey, {
                    kind: 'panel',
                    id: def.id,
                });
            }
            if (this._initialized) {
                this.$nextTick(() => {
                    const mounted = this.panelMounts[def.id];
                    if (mounted?.el) {
                        this.mountPanelToTarget(def.id, mounted.el);
                    }
                });
            }
        },

        unregisterPanel(id) {
            delete this.panelRegistry[id];
            for (const key of Object.keys(this.tabGroupPanelIds)) {
                const list = this.tabGroupPanelIds[key];
                const idx = list.indexOf(id);
                if (idx >= 0) list.splice(idx, 1);
            }
            this.unmountPanelFromTarget(id);
        },

        getPanelMeta(id) {
            const reg = this.panelRegistry[id];
            if (!reg) return { title: id };
            return {
                title: reg.title,
                icon: reg.icon,
                closable: reg.closable,
                ephemeral: !!reg.ephemeral,
            };
        },

        getEffectiveRegions() {
            if (!this.layoutState.root) {
                return this.layoutState.regions || createEmptyRegions();
            }
            return ensureLayoutStateRegions(
                this.layoutState,
                this.getPanelZonesMap(),
            );
        },

        getDockConstraintContext() {
            return createDockConstraintContext(this.layoutState, this.panelRegistry);
        },

        canDockPanelInZone(panelId, zone, hit = null) {
            if (!panelId) return true;
            if (hit) {
                return canDockPanelAtRootAwareHit(
                    panelId,
                    hit,
                    this.getDockConstraintContext(),
                    this.panelRegistry,
                );
            }
            if (!zone) return true;
            return canDockPanelInRootRegion(
                panelId,
                zone,
                this.panelRegistry,
            );
        },

        isPanelPinned(id) {
            return this.layoutState.panels[id]?.pinned !== false;
        },

        getPanelKind(id) {
            const stored = this.layoutState.panels[id]?.panelKind;
            if (stored) return stored;
            const reg = this.panelRegistry[id];
            return normalizePanelKind(reg?.panelKind, reg?.defaultZone);
        },

        isPanelCollapsed(id) {
            return !!this.collapsedPanels[id];
        },

        setPanelHostEl(id, el) {
            if (el) this.panelHostEls[id] = el;
            else delete this.panelHostEls[id];
        },

        mountPanelToTarget(panelId, targetEl) {
            const reg = this.panelRegistry[panelId];
            if (!reg?.default || !targetEl) return;

            // Tab groups reuse one content host; another panel may still own the DOM.
            for (const [id, m] of Object.entries(this.panelMounts)) {
                if (id !== panelId && m?.el === targetEl) {
                    vueRender(null, targetEl);
                    delete this.panelMounts[id];
                }
            }

            const prev = this.panelMounts[panelId];
            if (prev?.el && prev.el !== targetEl) {
                vueRender(null, prev.el);
            }

            const slotFn = reg.default;
            const vnode = createVNode({
                name: `MpDockPanelContent_${panelId}`,
                render: () => slotFn(),
            });
            vnode.appContext = this.$.appContext;
            vueRender(vnode, targetEl);
            this.panelMounts[panelId] = { el: targetEl, vnode };
        },

        unmountPanelFromTarget(panelId) {
            const m = this.panelMounts[panelId];
            if (m?.el) {
                vueRender(null, m.el);
            }
            delete this.panelMounts[panelId];
        },

        emitLayoutChange() {
            this._debouncedEmit?.();
        },

        syncPanelMounts() {
            const idsInTree = new Set(
                collectPanelIds(this.layoutState.root || null),
            );
            for (const id of Object.keys(this.panelMounts)) {
                if (!idsInTree.has(id)) {
                    this.unmountPanelFromTarget(id);
                }
            }
        },

        applyLayoutState(next, options = {}) {
            let root = next.root;
            const panels = next.panels || this.layoutState.panels || {};
            if (root) {
                root = applyPanelKindsToTabs(cloneTree(root), panels);
            }
            const regions = ensureLayoutStateRegions(
                { ...next, root },
                this.getPanelZonesMap(),
            );
            const edgeWrapOrder =
                next.rootEdgeOrder ??
                next.edgeWrapOrder ??
                this.layoutState.edgeWrapOrder ??
                [...DEFAULT_EDGE_WRAP_ORDER];
            this.layoutState = {
                ...next,
                root,
                regions,
                edgeWrapOrder,
                rootEdgeOrder: [...edgeWrapOrder],
            };
            this.syncPanelMounts();
            if (!options.skipEmit) this.emitLayoutChange();
        },

        updateSplitSizes(splitId, sizes) {
            const root = setSplitSizes(this.layoutState.root, splitId, sizes);
            this.applyLayoutState({ ...this.layoutState, root });
        },

        setActiveTab(tabsId, panelId) {
            const root = setActiveTab(this.layoutState.root, tabsId, panelId);
            this.applyLayoutState({ ...this.layoutState, root });
            this.$emit('panel-activate', panelId);
            this.$emit('tab-change', { tabsId, panelId });
            const tabsNode = findNode(root, tabsId)?.node;
            if (tabsNode?.contentMode === 'dynamic' && tabsNode.contentPanelId) {
                const m = this.panelMounts[tabsNode.contentPanelId];
                if (m?.el) {
                    this.mountPanelToTarget(tabsNode.contentPanelId, m.el);
                }
            }
        },

        onTabDrop(dropResult) {
            const { removedIndex, addedIndex, payload, targetTabsId } = dropResult;
            if (removedIndex == null || addedIndex == null || !payload) return;

            const { panelId, tabsId: sourceTabsId } = payload;

            if (targetTabsId && sourceTabsId !== targetTabsId) {
                const targetRegion = findRegionForNodeId(
                    this.layoutState.root,
                    this.layoutState.regions,
                    targetTabsId,
                );
                if (
                    targetRegion &&
                    !this.canDockPanelInZone(panelId, targetRegion)
                ) {
                    return;
                }
                const root = moveTabBetweenGroups(
                    this.layoutState.root,
                    sourceTabsId,
                    targetTabsId,
                    panelId,
                    addedIndex,
                );
                this.applyLayoutState({ ...this.layoutState, root });
                return;
            }

            const tabsNode = findNode(this.layoutState.root, sourceTabsId)?.node;
            if (!tabsNode || tabsNode.type !== 'tabs') return;
            if (removedIndex === addedIndex) return;

            const ids = [...tabsNode.panelIds];
            ids.splice(removedIndex, 1);
            ids.splice(addedIndex, 0, panelId);

            const root = reorderTabPanels(
                this.layoutState.root,
                sourceTabsId,
                ids,
                panelId,
            );
            this.applyLayoutState({ ...this.layoutState, root });
        },

        closePanel(panelId) {
            const reg = this.panelRegistry[panelId];
            if (reg?.ephemeral) {
                this.closeDynamicTab(panelId);
            } else {
                this.hidePanel(panelId);
            }
        },

        closeDynamicTab(panelId) {
            const next = closePanelInState(this.layoutState, panelId);
            this.unmountPanelFromTarget(panelId);
            delete this.collapsedPanels[panelId];
            if (this.panelRegistry[panelId]?.ephemeral) {
                delete this.panelRegistry[panelId];
            }
            this.applyLayoutState(next);
            this.$emit('dynamic-tab-close', panelId);
        },

        addCenterDynamicTab({
            id,
            title,
            icon,
            activate = true,
            closable = true,
        } = {}) {
            if (!id) return;

            this.panelRegistry[id] = {
                id,
                title: title || id,
                icon,
                closable,
                ephemeral: true,
                listInPanelsMenu: false,
                tabOnly: true,
                defaultZone: 'center',
                panelKind: 'center',
                pinned: true,
                default: null,
            };

            let root = cloneTree(this.layoutState.root);
            let tabsNode = findCenterDynamicTabsNode(root);

            if (!tabsNode) {
                const slot = findNode(root, CENTER_REGION_ID)?.node;
                const prevChild = slot?.child;
                const panelIds = prevChild?.type === 'panel'
                    ? [prevChild.panelId, id]
                    : [id];
                const contentPanelId = 'center-dynamic__content';
                tabsNode = createTabsNode(panelIds, activate ? id : panelIds[0], {
                    tabPlacement: TAB_PLACEMENT_TOP,
                });
                tabsNode.contentMode = 'dynamic';
                tabsNode.contentPanelId = contentPanelId;
                if (!this.tabGroupContentSlots[contentPanelId]) {
                    this.tabGroupContentSlots[contentPanelId] = () => null;
                }
                root = setCenterSlotChild(root, tabsNode);
            } else {
                root = appendPanelToTabsGroup(
                    root,
                    tabsNode.id,
                    id,
                    activate,
                );
            }

            const regions = syncRegionsAfterChange(
                root,
                this.getPanelZonesMap(),
            );
            this.applyLayoutState({ ...this.layoutState, root, regions });
            if (activate) {
                const updated = findCenterDynamicTabsNode(root);
                if (updated) {
                    this.$emit('tab-change', {
                        tabsId: updated.id,
                        panelId: id,
                    });
                }
                this.$emit('panel-activate', id);
            }
            this.$emit('dynamic-tab-add', id);
        },

        hidePanel(panelId) {
            const next = hidePanelInState(this.layoutState, panelId);
            this.unmountPanelFromTarget(panelId);
            delete this.collapsedPanels[panelId];
            this.applyLayoutState(next);
            this.$emit('panel-hide', panelId);
        },

        showPanel(panelId) {
            const next = showPanelInState(this.layoutState, panelId);
            delete this.collapsedPanels[panelId];
            this.applyLayoutState(next);
            this.$emit('panel-show', panelId);
            this.$nextTick(() => {
                const m = this.panelMounts[panelId];
                if (m?.el) this.mountPanelToTarget(panelId, m.el);
            });
        },

        expandPanel(panelId) {
            delete this.collapsedPanels[panelId];
            this.$emit('panel-activate', panelId);
        },

        togglePanelPinned(panelId) {
            const panels = { ...this.layoutState.panels };
            const cur = panels[panelId] || {};
            panels[panelId] = { ...cur, pinned: !this.isPanelPinned(panelId) };
            if (panels[panelId].pinned) {
                delete this.collapsedPanels[panelId];
            }
            this.applyLayoutState({ ...this.layoutState, panels });
        },

        onPanelFocusChange({ panelId, focused }) {
            if (focused) {
                delete this.collapsedPanels[panelId];
                this.$emit('panel-activate', panelId);
            } else if (!this.isPanelPinned(panelId)) {
                this.collapsedPanels = {
                    ...this.collapsedPanels,
                    [panelId]: true,
                };
            }
        },

        getDockDragContext() {
            const pending = this._pendingDrag || {};
            return {
                panelId: this._dragPanelId || pending.panelId,
                groupNodeId:
                    this.dockDrag.groupNodeId || pending.groupNodeId,
                dragMode: pending.dragMode || this.dockDrag.dragMode || 'panel',
                panelIds:
                    pending.panelIds ||
                    this.dockDrag.panelIds ||
                    [],
            };
        },

        getDockBodyRect() {
            const body = this.$refs.rootEl?.querySelector('.mp-dock-body');
            return body?.getBoundingClientRect() ?? null;
        },

        refreshDropZones() {
            const body = this.$refs.rootEl?.querySelector('.mp-dock-body');
            const dragContext = this.getDockDragContext();
            const panelId = dragContext.panelId;
            const dragPanelIds =
                dragContext.dragMode === 'group'
                    ? dragContext.panelIds || []
                    : panelId
                      ? [panelId]
                      : [];
            const ctx = this.getDockConstraintContext();
            const registry = this.panelRegistry;
            return buildDropZoneList(
                body,
                (id) => this.getPanelMeta(id),
                dragContext,
                (_zone, hit) => {
                    if (!dragPanelIds.length) return true;
                    const rootRegion = resolveRootRegionFromHit(hit, ctx);
                    return canDockPanelsInRootRegion(
                        dragPanelIds,
                        rootRegion,
                        registry,
                    );
                },
            );
        },

        zoneHoverKey(hit) {
            if (!hit) return null;
            if (hit.tier === 'root') return `root::${hit.region}`;
            return `${hit.nodeId}::${hit.zone}`;
        },

        dropPayloadFromZone(z) {
            if (!z) return null;
            const targetPanelId = z.panelId || null;
            return {
                tier: z.tier,
                region: z.region,
                nodeId: z.nodeId,
                zone: z.zone,
                hostType: z.hostType,
                panelId: targetPanelId,
                targetPanelId,
            };
        },

        activateDockDrag(payload) {
            const {
                panelId,
                title,
                clientX,
                clientY,
                dragMode = 'panel',
                groupNodeId = null,
                panelIds = [],
            } = payload;
            this._dragPanelId = panelId;
            this._lastPointerX = clientX;
            this._lastPointerY = clientY;
            this.dockDrag = {
                active: true,
                panelId,
                dragMode,
                groupNodeId,
                panelIds: panelIds.length ? panelIds : panelId ? [panelId] : [],
                dropZones: this.refreshDropZones(),
                hoverZoneKey: null,
                commitHit: null,
                forbiddenHover: false,
                ghost: {
                    title: title || panelId,
                    x: clientX + 12,
                    y: clientY + 12,
                },
            };
        },

        updateDockDragHover(dropZones, clientX, clientY, preferredHoverKey = null) {
            const bodyRect = this.getDockBodyRect();
            const hoverHit = hitTestDropZone(
                dropZones,
                clientX,
                clientY,
                { allowedOnly: false, bodyRect },
            );
            const allowedHit = hitTestDropZone(
                dropZones,
                clientX,
                clientY,
                { preferredHoverKey, bodyRect },
            );
            const forbiddenHover = !!(
                hoverHit &&
                hoverHit.allowed === false &&
                !allowedHit
            );
            if (forbiddenHover) {
                document.body.style.cursor = 'not-allowed';
            } else {
                document.body.style.cursor = '';
            }
            return {
                hoverZoneKey: forbiddenHover
                    ? null
                    : this.zoneHoverKey(allowedHit),
                forbiddenHover,
                commitHit: allowedHit || null,
            };
        },

        resetDockDragCursor() {
            document.body.style.cursor = '';
        },

        onDropZoneHover({ key, zone }) {
            if (!this.dockDrag.active) return;
            if (zone?.allowed !== true) return;
            const panelId = this._dragPanelId || this.dockDrag.panelId;
            if (
                panelId &&
                !canDockPanelAtRootAwareHit(
                    panelId,
                    zone,
                    this.getDockConstraintContext(),
                    this.panelRegistry,
                )
            ) {
                return;
            }
            this.dockDrag = {
                ...this.dockDrag,
                hoverZoneKey: key,
                commitHit: zone,
                forbiddenHover: false,
            };
            this.resetDockDragCursor();
        },

        onDropZoneDrop(zone) {
            if (!this.dockDrag.active || zone?.allowed !== true) return;
            const panelId = this._dragPanelId || this.dockDrag.panelId;
            if (
                panelId &&
                !canDockPanelAtRootAwareHit(
                    panelId,
                    zone,
                    this.getDockConstraintContext(),
                    this.panelRegistry,
                )
            ) {
                return;
            }
            this.dockDrag = {
                ...this.dockDrag,
                commitHit: zone,
                forbiddenHover: false,
            };
            const payload = this.dropPayloadFromZone(zone);
            if (payload) this.commitDockDrop(payload);
        },

        startDockDrag(payload) {
            this.teardownDockDrag();
            this._pendingDrag = payload;
            const startX = payload.clientX;
            const startY = payload.clientY;

            const move = (ev) => {
                if (!this.dockDrag.active && this._pendingDrag) {
                    const dx = ev.clientX - startX;
                    const dy = ev.clientY - startY;
                    if (
                        Math.hypot(dx, dy) < DOCK_DRAG_THRESHOLD_PX
                    ) {
                        return;
                    }
                    this.activateDockDrag(this._pendingDrag);
                }
                this._lastPointerX = ev.clientX;
                this._lastPointerY = ev.clientY;
                if (this.dockDrag.active && this.dockDrag.ghost) {
                    const dropZones = this.refreshDropZones();
                    const { hoverZoneKey, forbiddenHover, commitHit } =
                        this.updateDockDragHover(
                            dropZones,
                            ev.clientX,
                            ev.clientY,
                            this.dockDrag.hoverZoneKey,
                        );
                    this.dockDrag = {
                        ...this.dockDrag,
                        dropZones,
                        hoverZoneKey,
                        commitHit,
                        forbiddenHover,
                        ghost: {
                            ...this.dockDrag.ghost,
                            x: ev.clientX + 12,
                            y: ev.clientY + 12,
                        },
                    };
                }
            };

            const up = (ev) => {
                if (this.dockDrag.active) {
                    const hit = hitTestDropZone(
                        this.dockDrag.dropZones,
                        ev?.clientX ?? this._lastPointerX,
                        ev?.clientY ?? this._lastPointerY,
                        { bodyRect: this.getDockBodyRect() },
                    );
                    if (hit?.allowed === true) {
                        const panelId =
                            this._dragPanelId || this.dockDrag.panelId;
                        const ctx = this.getDockConstraintContext();
                        const registry = this.panelRegistry;
                        const mayDrop =
                            !panelId ||
                            canDockPanelAtRootAwareHit(
                                panelId,
                                hit,
                                ctx,
                                registry,
                            );
                        if (mayDrop) {
                            const payload = this.dropPayloadFromZone(hit);
                            if (payload) {
                                this.commitDockDrop(payload);
                                return;
                            }
                        }
                    }
                }
                this._pendingDrag = null;
                this.teardownDockDrag();
            };

            this._dockDragHandlers = { move, up };
            window.addEventListener('pointermove', move);
            window.addEventListener('pointerup', up);
            window.addEventListener('pointercancel', up);
        },

        teardownDockDrag() {
            if (this._dockDragHandlers) {
                const { move, up } = this._dockDragHandlers;
                window.removeEventListener('pointermove', move);
                window.removeEventListener('pointerup', up);
                window.removeEventListener('pointercancel', up);
                this._dockDragHandlers = null;
            }
            this.resetDockDragCursor();
            window.getSelection?.()?.removeAllRanges?.();
            if (this.dockDrag.active) {
                this.dockDrag = {
                    active: false,
                    panelId: null,
                    dragMode: 'panel',
                    groupNodeId: null,
                    panelIds: [],
                    dropZones: [],
                    hoverZoneKey: null,
                    commitHit: null,
                    forbiddenHover: false,
                    ghost: null,
                };
            }
            this._dragPanelId = null;
        },

        commitDockDrop({
            tier,
            region,
            nodeId,
            zone,
            hostType,
            targetPanelId,
        }) {
            if (!zone || !this.dockDrag.active) return;
            if (this.dockDrag.forbiddenHover && !this.dockDrag.commitHit) {
                this._pendingDrag = null;
                this.teardownDockDrag();
                return;
            }

            const ctx = this._pendingDrag || {};
            const dragMode = ctx.dragMode || this.dockDrag.dragMode || 'panel';
            const panelId = this._dragPanelId || this.dockDrag.panelId;
            const panelZoneMap = this.getPanelZonesMap();
            const dragPanelIds = dragMode === 'group'
                ? (ctx.panelIds || this.dockDrag.panelIds || []).filter(Boolean)
                : panelId
                  ? [panelId]
                  : [];
            if (dragMode === 'panel' && !panelId) return;
            if (dragMode === 'group' && !ctx.groupNodeId) return;

            const payload = {
                tier,
                region,
                nodeId,
                zone,
                hostType,
                panelId: targetPanelId,
                targetPanelId,
            };
            const intent = buildDropIntent(
                payload,
                this.getDockConstraintContext(),
                { dragMode },
            );
            if (!intent) {
                this._pendingDrag = null;
                this.teardownDockDrag();
                return;
            }
            if (
                !canDockPanelsInRootRegion(
                    dragPanelIds,
                    intent.targetRootRegion,
                    this.panelRegistry,
                )
            ) {
                this._pendingDrag = null;
                this.teardownDockDrag();
                return;
            }

            const reduced = reduceDockDrop(this.layoutState, {
                intent,
                dragState: {
                    dragMode,
                    groupNodeId: ctx.groupNodeId || null,
                },
                panelId,
                panelZoneMap,
            });
            const ok = !!reduced.ok;

            this._pendingDrag = null;
            this.teardownDockDrag();
            if (ok) this.layoutRenderKey += 1;
            this.applyLayoutState({
                ...this.layoutState,
                root: reduced.root,
                regions: reduced.regions,
                panels: reduced.panels,
                edgeWrapOrder: reduced.edgeWrapOrder,
                rootEdgeOrder: reduced.edgeWrapOrder,
            });

            const activateId =
                dragMode === 'group'
                    ? ctx.panelIds?.[0] || panelId
                    : panelId;
            if (activateId && ok) {
                this.$emit('panel-activate', activateId);
                this.$nextTick(() => {
                    const ids =
                        dragMode === 'group'
                            ? ctx.panelIds || []
                            : [panelId];
                    for (const id of ids) {
                        const m = this.panelMounts[id];
                        if (m?.el) this.mountPanelToTarget(id, m.el);
                    }
                });
            }
        },

        serializeLayout() {
            return exportLayout(this.layoutState, {
                ephemeralPanelIds: this.ephemeralPanelIds,
            });
        },

        deserializeLayout(json) {
            const ids = Object.keys(this.panelRegistry).filter(
                (id) => !this.panelRegistry[id]?.isContentHost,
            );
            const imported = importLayout(json, {
                registeredPanelIds: ids,
                defaultOrientation: this.defaultOrientation,
                panelZones: this.getPanelZonesMap(),
                panelKinds: this.getPanelKindsMap(),
                regionBlueprints: this.assembleRegionBlueprints(),
            });
            this.registerTabGroupContentSlots(imported.root);
            this.applyLayoutState(imported);
        },

        resetLayout() {
            this._debouncedEmit?.cancel?.();
            if (this.persistKey) {
                try {
                    localStorage.removeItem(PERSIST_PREFIX + this.persistKey);
                } catch (_) {
                    /* ignore */
                }
            }
            const visible = Object.keys(this.panelRegistry).filter(
                (id) => !(this.layoutState.hiddenPanelIds || []).includes(id),
            );
            const registered = Object.keys(this.panelRegistry).filter(
                (id) => !this.panelRegistry[id]?.isContentHost,
            );
            const imported = importLayout(null, {
                registeredPanelIds: visible.length ? visible : registered,
                defaultOrientation: this.defaultOrientation,
                panelZones: this.getPanelZonesMap(),
                panelKinds: this.getPanelKindsMap(),
                regionBlueprints: this.assembleRegionBlueprints(),
            });
            for (const id of Object.keys(this.panelMounts)) {
                this.unmountPanelFromTarget(id);
            }
            this.registerTabGroupContentSlots(imported.root);
            this.layoutRenderKey += 1;
            this.applyLayoutState(imported);
            const payload = exportLayout(this.layoutState, {
                ephemeralPanelIds: this.ephemeralPanelIds,
            });
            this.$emit('update:layout', payload);
            this.$emit('layout-change', payload);
        },

        setPanelPinned(panelId, pinned) {
            const panels = { ...this.layoutState.panels };
            panels[panelId] = { ...(panels[panelId] || {}), pinned: !!pinned };
            if (pinned) delete this.collapsedPanels[panelId];
            this.applyLayoutState({ ...this.layoutState, panels });
        },

        getPanelState(panelId) {
            return {
                ...(this.layoutState.panels[panelId] || {}),
                hidden: (this.layoutState.hiddenPanelIds || []).includes(
                    panelId,
                ),
                collapsed: !!this.collapsedPanels[panelId],
            };
        },
    },
};
</script>
